import styled, { css } from 'styled-components';
import {
  FlexColumn,
  Flex,
  Collapsible,
  formatNumber,
  useDownloadFile,
  useIsXOverflow,
  Tooltip,
  useAsyncFunctionProgress,
} from '@equifinance/shared';
import { useRef } from 'react';
import { useCreditContext } from '../../../contexts/credit';
import Section from '../../Account/components/Section';
import { DownloadIcon, DownloadLink, HiddedA } from '../../Products/ProductComponents';
import useDocumentsApi from '../../../api/useDocumentsApi';

export const DisbursementSummaryRefName = 'disbursement-table-details';

const DisbursementDetailsHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.tertiary[5]};
`}`;
const DisbursementDetailsHeaderTitleCss = css`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
`}`;

const Container = styled(FlexColumn)`${({ theme }) => `
  margin: ${theme.padding[2]} ${theme.padding[4]};
  gap: ${theme.padding[5]};
`}`;

const DownloadButton = styled(Flex)`${({ theme }) => `
  align-items: center;
  font-weight: ${theme.fontWeight.bold};
  margin-left: auto;
`}`;

const TableContainer = styled(FlexColumn)`${({ theme }) => `
  max-height: ${theme.disbursementSummaryHeight};
  gap: ${theme.padding[1]};
  overflow: auto;
`}`;

const listHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.neutral[9]};
  padding: 0;

  .mi-chevron-up, .mi-chevron-down {
    position: absolute;
    right: ${theme.padding[4]};
  }
`}`;

const listBodyCss = css`${({ theme }) => `
  padding: 0;
  padding-top: ${theme.padding[1]};
`}`;

const TableCell = styled.div`${({ theme }) => `
  box-sizing: border-box;
  padding: ${theme.padding[3]} ${theme.padding[2]};
  width: calc(100% / 6);
  font-size: ${theme.fontSize[1]};
  line-height: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[2]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:first-child{
    padding-left: ${theme.padding[4]};
  }
`}`;

const TableCellExpanded = styled(TableCell)`
  width: calc((100% / 6) * 2);
  flex-grow: 1;
`;

const TableHeader = styled(Flex)`${({ theme }) => `
  background: ${theme.colours.neutral[8]};
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: ${theme.fontWeight.bold};
  border-radius: ${theme.borderRadius[2]} ${theme.borderRadius[2]} 0 0;
`}`;

const TableFooter = styled(Flex)`${({ theme }) => `
  background: ${theme.colours.neutral[10]};
  position: sticky;
  bottom: 0;
  z-index: 1;
  border-bottom: ${theme.borderWidth[1]} solid ${theme.colours.neutral[7]};
  ${TableCell} {
    span {
      font-weight: ${theme.fontWeight.bold};
    }
  }
`}`;

const TableBody = styled(FlexColumn)`${({ theme }) => `
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.padding[1]};
`}`;

const TableRow = styled(Flex)`${({ theme }) => `
  background: ${theme.colours.neutral[9]};
  align-items: center;
`}`;

const CollapsibleRow = styled(Flex)`${({ theme }) => `
  background: ${theme.colours.primary[8]};
  width: 100%;

  ${TableCell} {
    &:last-child {
      padding-right: ${theme.padding[8]};
    }
  }
`}`;

function formatSortCode(sortCode?: string, defaultValue: string = '-') {
  return sortCode?.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3') || defaultValue;
}

const formatCurrency = (value?: number) => {
  if (value == null || Number.isNaN(value)) {
    return '-';
  }

  return `£${formatNumber(value, 2)}`;
};

function TableCellWithTooltip({ id, value }: { id: string, value: string }) {
  const cellRef = useRef<HTMLDivElement>(null);

  const isTruncated = useIsXOverflow(cellRef);

  return (
    <TableCell id={`disbursement-cell-${id}`} ref={cellRef}>
      {value}
      {isTruncated && (
        <Tooltip
          id={`disbursement-cell-${id}`}
          html={value}
          place="top"
        />
      )}
    </TableCell>
  );
}

export default function DisbursementDetails() {
  const { credit: { disbursementSummary }, id: applicationId } = useCreditContext();
  const { postGenerateSummaryOfDisbursement } = useDocumentsApi();
  const {
    downloadFile,
    downloadFileRef,
  } = useDownloadFile();

  const disbursementGroups = disbursementSummary
    ?.disbursementGroups
    ?.map((group) => ({
      ...group,
      isApplicantsDirect: group.name === 'Applicants direct',
      defaultValue: group.name === 'Applicants direct' ? 'N/A' : '-',
      disbursements: disbursementSummary
        .disbursements
        .filter((disbursement) => disbursement.groupId === group.id),
    }));

  const onGenerateDisbursementSummary = async () => {
    const document = await postGenerateSummaryOfDisbursement(applicationId);

    if (document) {
      downloadFile(document.fileName, document.documentData);
    }
  };

  const { inProgress, execute: executeGenerateDisbursementSummary } = useAsyncFunctionProgress(onGenerateDisbursementSummary);

  return (
    <Section
      id={disbursementSummary.id}
      refName={DisbursementSummaryRefName}
      title="Summary of Disbursement"
      headerCss={DisbursementDetailsHeaderCss}
      titleCss={DisbursementDetailsHeaderTitleCss}
      collapsible={false}
      titleIcon={<></>}
    >
      <Container>
        <DownloadButton>
          <DownloadIcon disabled={inProgress} className="mi-download" />
          <DownloadLink
            disabled={inProgress}
            onClick={(e) => {
              if (inProgress) return;
              e.stopPropagation();
              executeGenerateDisbursementSummary();
            }}
            aria-hidden="true"
          >
            Download Summary of disbursement
          </DownloadLink>
        </DownloadButton>
        <TableContainer>
          <TableHeader>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Sort code</TableCell>
            <TableCell>Account number</TableCell>
            <TableCell>Reference</TableCell>
          </TableHeader>
          {disbursementGroups.map((group) => (
            <Collapsible
              key={group.id}
              id={`${group.id}-disbursement-group`}
              level={2}
              collapsed={false}
              headerCss={listHeaderCss}
              bodyCss={listBodyCss}
              title={(
                <CollapsibleRow>
                  <TableCellExpanded>{group.name}</TableCellExpanded>
                  {group.isApplicantsDirect && (
                    <>
                      <TableCell>{formatCurrency(group.amount)}</TableCell>
                      <TableCell>{formatSortCode(group.sortCode)}</TableCell>
                      <TableCell>{group.accountNumber || '-'}</TableCell>
                      <TableCellWithTooltip id={group.id} value={group.reference || '-'} />
                    </>
                  )}
                </CollapsibleRow>
              )}
            >
              <TableBody>
                {!group.disbursements.length && (
                  <TableRow>
                    <TableCellExpanded>Nothing to display</TableCellExpanded>
                  </TableRow>
                )}
                {group.disbursements.map((disbursement) => (
                  <TableRow key={disbursement.id}>
                    <TableCellWithTooltip id={`${disbursement.id}-name`} value={disbursement.name || '-'} />
                    <TableCell>{disbursement.type || '-'}</TableCell>
                    <TableCell>{formatCurrency(disbursement.amount)}</TableCell>
                    <TableCell>{formatSortCode(disbursement.sortCode, group.defaultValue)}</TableCell>
                    <TableCell>{disbursement.accountNumber || group.defaultValue}</TableCell>
                    <TableCellWithTooltip id={`${disbursement.id}-reference`} value={disbursement.reference || group.defaultValue} />
                  </TableRow>
                ))}
              </TableBody>
            </Collapsible>
          ))}
          <TableFooter>
            <TableCell />
            <TableCell>
              <span>Total</span>
            </TableCell>
            <TableCellExpanded>{formatCurrency(disbursementSummary.total)}</TableCellExpanded>
          </TableFooter>
        </TableContainer>
        <HiddedA ref={downloadFileRef} />
      </Container>
    </Section>
  );
}
